import { useEffect, useState } from 'react';
import { analysisApi, formatCurrency } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';
import { priceRangeLine } from '../utils/topProducts';
import { openScanInNewTab } from '../utils/openScan';

// The orders behind one product row of המוצרים המובילים ברכש — the documents
// whose lines make up the row's amount. Fetched with the row's own
// supplier_id + product_key and the SAME window and supplier filter the row came
// from, so they add up to the row (lib/topProducts.js topProductOrders). Used by
// the phone's bottom sheet (TopProductSheet) and the report's inline sub-lines
// on wider screens (TopProductOrdersPanel, below).
export function useProductOrders(product, params) {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setOrders(null);
    setError(false);
    analysisApi.getTopProductOrders({
      ...params,
      supplier_id: product.supplier_id,
      key: product.product_key,
    })
      .then(r => { if (alive) setOrders(r.data || []); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
    // The fields it reads, not the objects: params is rebuilt by the caller
    // each render, and a reload hands back new row objects for the same
    // product — an open panel must not refetch for either (review, #311).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.supplier_id, product.product_key, params.date_from, params.date_to, params.supplier_ids]);

  return { orders, error };
}

export const qtyText = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 3 });
const money = (n) => (n == null ? '—' : formatCurrency(n));

export function OrdersState({ orders, error }) {
  if (error) return <p className="tps-muted">שגיאה בטעינת ההזמנות.</p>;
  if (orders == null) return <div className="loading-container" style={{ minHeight: 80 }}><div className="spinner" /></div>;
  if (orders.length === 0) return <p className="tps-muted">לא נמצאו הזמנות.</p>;
  return null;
}

// Which ends of the period's range an order holds — compared on the order's
// OWN lowest and highest line price (server: line extremes, the same
// expression as the row's min/max), not its averaged unit_price: an invoice
// with the product at ₪10 and ₪12 holds both ends though it averages ₪11
// (Codex, PR #312). Prices are 2dp, so equality is to the agora.
const atPrice = (a, b) => a != null && b != null && Math.abs(a - b) < 0.005;
function rangeTags(order, range) {
  if (range?.kind !== 'range') return [];
  const low = order.min_price ?? order.unit_price;
  const high = order.max_price ?? order.unit_price;
  return [atPrice(low, range.min) && 'מינ׳', atPrice(high, range.max) && 'מקס׳'].filter(Boolean);
}

// One product's orders as sub-lines under its row in the REPORT, from 641px —
// the same drill-down as רכש לפי ספקים (owner, 22/10/2026; it replaced a
// centred dialog). Just the orders table (owner, 23/10/2026):
//   - the document number is BLUE when there is a scan and opens it in a new
//     tab (a button styled as a link: the scan is fetched with the user's
//     login, so it cannot be a plain href); plain grey when there is none.
//     Nothing here navigates away from the report.
//   - the period's lowest and highest unit price carry a small מינ׳ / מקס׳
//     tag on the order that paid it — which invoice to show the supplier
//     (none with one purchase or one price all period, priceRangeLine).
//   - a סה״כ footer under the quantity and amount columns, instead of a
//     summary line repeating the row above.
export function TopProductOrdersPanel({ product, params }) {
  const { orders, error } = useProductOrders(product, params);
  const range = priceRangeLine(product);
  return (
    <div className="tp-orders-panel">
      {range?.kind === 'units' && (
        <div className="tps-muted">פער חריג בין המחירים — ייתכן שמדובר ביחידות שונות</div>
      )}
      <OrdersState orders={orders} error={error} />
      {orders?.length > 0 && (
        <table className="tps-orders">
          <thead>
            <tr>
              <th className="tps-num tps-col-date">תאריך</th>
              <th>מסמך</th>
              <th className="tps-num tps-col-qty">כמות</th>
              <th className="tps-num tps-col-price">מחיר ליח׳</th>
              <th className="tps-num tps-col-sum">סכום (ללא מע״מ)</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => {
              const tags = rangeTags(o, range);
              return (
                <tr key={o.doc_id}>
                  <td className="tps-num">{formatDateShort(o.date)}</td>
                  <td>
                    {o.file_path ? (
                      <button type="button" className="tps-doc-link" title="פתיחת המסמך בלשונית חדשה" onClick={() => openScanInNewTab(o.file_path)}>
                        {o.doc_num || `#${o.doc_id}`}
                      </button>
                    ) : (o.doc_num || '—')}
                    {' '}<span className="tps-muted tps-doc-type">· {o.doc_type}</span>
                  </td>
                  <td className="tps-num">{qtyText(o.qty)}</td>
                  <td className="tps-num">
                    {tags.map(t => <span key={t} className="tps-tag">{t}</span>)}
                    {money(o.unit_price)}
                  </td>
                  <td className="tps-num">{formatCurrency(o.spend)}</td>
                </tr>
              );
            })}
          </tbody>
          {/* The orders are reconciled to the row (lib/topProducts.js), so
              the row's own totals ARE their sum. */}
          <tfoot>
            <tr>
              <td colSpan={2}>סה״כ</td>
              <td className="tps-num">{qtyText(product.qty)}</td>
              <td />
              <td className="tps-num">{formatCurrency(product.spend)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
